import { useMemo } from "react";
import { Token } from "store/global/TokenEnum";
import { TokenMetadata } from "store/global/getTokenMetadata";
import { Amount } from "types/amount";
import { OrderType } from "utils/orderTypeUtils";
import { ActivityStore } from "../store/activity/ActivityStore";
import { TRANSACTION_STATUSES, TransactionDetail } from "../types/history";

export const useTradeHistoryGroupedData = () => {
  const history = ActivityStore.useState(s => s.history);
  const tokens = ActivityStore.useState(s => s.tokens);

  /*
    Groups the data returned from getHistory.ts by Month
    This is so we can show the Month/Year above the trades which were made in that Month/year.
   */
  const groupedData = useMemo(() => {
    if (history.error != null) return null;
    if (history.isLoading || history.data === undefined) {
      return getMockDataActivitySkeleton(tokens ?? {}); // populate skeletons in table if history not returned yet
    }
    const sortedData = [...Object.values(history.data.results)].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );

    return sortedData.reduce(
      (groups: Record<string, Array<TransactionDetail>>, item: TransactionDetail) => {
        const month = item.timestamp.getMonth();
        const year = item.timestamp.getFullYear();
        const key = `${year}-${month}`;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
        return groups;
      },
      {}
    );
  }, [history, tokens]);

  return groupedData;
};

// Helpers

// Mock data for activity table to render skeletons
const getMockTransactionDetail =
  (tokens: TokenMetadata) =>
  (month: number): TransactionDetail => {
    const mockTokenName = Object.keys(tokens)[0] as Token;
    return {
      chain_id: 1,
      status: TRANSACTION_STATUSES.SETTLED,
      type: OrderType.OneToMany,
      sender: `0x0`,
      receiver: `0x0`,
      sellTokens: { [mockTokenName]: {} },
      buyTokens: { [mockTokenName]: {} },
      timestamp: new Date(`2000-${month}-1`),
      volumeUsd: new Amount("0"),
      gasFeeUsd: 0.123,
      txHash: "0",
    };
  };

export const getMockDataActivitySkeleton = (
  tokens: TokenMetadata
): Record<string, Array<TransactionDetail>> => {
  const getMockTransactionByMonth = getMockTransactionDetail(tokens);
  return {
    "2000-3": Array(6).fill(getMockTransactionByMonth(4)),
    "2000-2": Array(4).fill(getMockTransactionByMonth(3)),
    "2000-1": Array(8).fill(getMockTransactionByMonth(2)),
    "2000-0": Array(4).fill(getMockTransactionByMonth(1)),
  };
};
